import { z } from 'zod';
import { JobStatuses } from '../statuses';
import { listQuerySchema, optionalTrimmed, trimmed, uuid } from './common';
const enumOf = (values) => z.enum(values);
export const regionSchema = z.object({
    prefecture: z.string().trim().min(1).max(20),
    city: z.string().trim().max(40).optional(),
});
/** 収集条件（docs/02-functional-spec.md F03.1） */
export const searchConditionInputSchema = z.object({
    productId: uuid.optional(),
    keywords: z.array(z.string().trim().min(1).max(80)).min(1, 'キーワードを 1 つ以上入力してください').max(20),
    keywordMode: z.enum(['and', 'or']).default('or'),
    excludeKeywords: z.array(z.string().trim().min(1).max(80)).max(50).optional(),
    industries: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
    regions: z.array(regionSchema).max(50).optional(),
    limitCount: z.number().int().min(1).max(5000),
    excludeDomains: z.array(z.string().trim().min(1).max(253)).max(500).optional(),
    /** 既存企業の扱い：除外／再収集して情報更新 */
    existingPolicy: z.enum(['skip', 'recrawl']).default('skip'),
});
export const saveSearchConditionSchema = searchConditionInputSchema.extend({
    name: trimmed(80, '条件の名前を入力してください'),
});
export const searchConditionSchema = saveSearchConditionSchema.extend({
    id: uuid,
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
});
export const searchPreviewSchema = z.object({
    estimatedCount: z.number().int(),
    /** 実際に取得したサンプル */
    samples: z.array(z.object({
        title: z.string(),
        url: z.string(),
        snippet: z.string().nullable(),
        /** 企業サイト／EC／媒体／不明の判定（docs/02-functional-spec.md F03.3） */
        siteType: z.enum(['company', 'ec', 'portal', 'social', 'unknown']),
        excluded: z.boolean(),
        excludeReason: z.string().nullable(),
        alreadyRegistered: z.boolean(),
    })),
    queries: z.array(z.string()),
    apiCallCount: z.number().int(),
});
export const collectionProgressSchema = z.object({
    total: z.number().int(),
    processed: z.number().int(),
    created: z.number().int(),
    duplicate: z.number().int(),
    excluded: z.number().int(),
    error: z.number().int(),
});
export const collectionRunSchema = z.object({
    id: uuid,
    name: z.string(),
    status: enumOf(JobStatuses.values),
    params: searchConditionInputSchema.partial(),
    progress: collectionProgressSchema,
    apiCallCount: z.number().int(),
    jobRunId: uuid.nullable(),
    message: z.string().nullable(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
});
export const collectionListQuerySchema = listQuerySchema.extend({
    status: z.string().optional(),
});
export const startCollectionSchema = searchConditionInputSchema.extend({
    searchConditionId: uuid.optional(),
    name: optionalTrimmed(80),
});
// ---------------------------------------------------------------- lists
export const listFilterSchema = z.record(z.string(), z.unknown());
export const createListSchema = z
    .object({
    name: trimmed(80, 'リスト名を入力してください'),
    description: optionalTrimmed(500),
    type: z.enum(['static', 'dynamic']),
    filterJson: listFilterSchema.optional(),
    companyIds: z.array(uuid).max(10000).optional(),
})
    .refine((v) => v.type !== 'dynamic' || v.filterJson != null, {
    message: '条件リストには絞り込み条件が必要です',
    path: ['filterJson'],
});
export const updateListSchema = z.object({
    name: optionalTrimmed(80),
    description: optionalTrimmed(500),
    filterJson: listFilterSchema.optional(),
});
export const listSummarySchema = z.object({
    id: uuid,
    name: z.string(),
    description: z.string().nullable(),
    type: z.enum(['static', 'dynamic']),
    filterJson: z.record(z.string(), z.unknown()).nullable(),
    companyCount: z.number().int(),
    usedByCampaignCount: z.number().int(),
    createdBy: z.object({ id: uuid, name: z.string().nullable() }).nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const listItemsSchema = z
    .object({
    companyIds: z.array(uuid).max(10000).optional(),
    filter: listFilterSchema.optional(),
})
    .refine((v) => (v.companyIds?.length ?? 0) > 0 || v.filter != null, {
    message: '追加する企業を指定してください',
});
// ---------------------------------------------------------------- products
export const productCaseSchema = z.object({
    id: uuid.optional(),
    title: trimmed(120, '事例のタイトルを入力してください'),
    industry: optionalTrimmed(60),
    companySize: optionalTrimmed(40),
    challenge: optionalTrimmed(1000),
    result: optionalTrimmed(1000),
    /** 公開不可の事例は AI 入力から除外する（docs/02-functional-spec.md F02.2） */
    isPublic: z.boolean().default(true),
    displayOrder: z.number().int().min(0).max(999).optional(),
});
export const createProductSchema = z.object({
    name: trimmed(80, '商材名を入力してください'),
    summary: trimmed(2000, '概要を入力してください'),
    strengths: z
        .array(trimmed(200))
        .min(1, '強みを 1 つ以上入力してください')
        .max(20),
    pricing: optionalTrimmed(1000),
    pricingPublic: z.boolean().default(false),
    painPoints: z.array(trimmed(200)).max(20).optional(),
    targetCriteria: z
        .object({
        industries: z.array(z.string().trim().max(60)).max(20).optional(),
        regions: z.array(regionSchema).max(50).optional(),
        employeeSizes: z.array(z.string().trim().max(20)).max(10).optional(),
        keywords: z.array(z.string().trim().max(80)).max(20).optional(),
    })
        .default({}),
    ngPhrases: z.array(trimmed(60)).max(50).optional(),
    cases: z.array(productCaseSchema).max(50).optional(),
});
export const updateProductSchema = createProductSchema.partial();
export const productSchema = createProductSchema.extend({
    id: uuid,
    status: z.enum(['active', 'archived']),
    cases: z.array(productCaseSchema.extend({ id: uuid })),
    usedByCampaignCount: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
//# sourceMappingURL=collections.js.map